import type { TDutyStatusLogStatus } from "./interfaces/dutyStatusLog";
import type { IStringHash, ITypedHash } from "./interfaces/entity";
import type { THosStatus } from "./interfaces/statues";
import type { IViolation } from "./interfaces/violation";
import { STATUS_LOG_TYPES } from "./rules/helpers";

export type TFontWeight = "normal" | "bold";
type TDrawDriver = "canvas" | "svg";
type TGraphClickHandler = (id: string, caller?: (e: Event) => void) => void;

type TView = SVGElement | HTMLCanvasElement;

interface IDrawDriver<T extends TView> {
    createCanvas: (width: number, height: number) => void;
    getCanvas: () => T;
    setCanvas: (canvas: T) => void;
    setDescriptorId: (descriptorId: string) => void;
    rect: (
        x: number,
        y: number,
        width: number,
        height: number,
        style: IStyles,
        id?: string,
        className?: string,
        clickHandler?: TGraphClickHandler | null,
        detailsLink?: string,
        containerId?: string
    ) => void;
    line: (x1: number, y1: number, x2: number, y2: number, color: string, width: number, containerId?: string) => void;
    text: (
        x: number,
        y: number,
        text: string,
        fontSize: number,
        fontWeight?: TFontWeight,
        className?: string,
        color?: string,
        id?: string,
        containerId?: string
    ) => void;
    pattern: (
        x?: number,
        y?: number,
        width?: number,
        height?: number,
        id?: string,
        containerId?: string,
        patternCanvas?: CanvasImageSource
    ) => void;
    group?: (id: string, attributes?: IStringHash, containerId?: string) => void;
    getWidth: () => number;
    getHeight: () => number;
    removeListeners: () => void;
}

const isSvgDrawDriver = (driver: SVGDrawDriver | CanvasDrawDriver): driver is SVGDrawDriver => {
    return typeof (<SVGDrawDriver>driver).group === "function";
};

export interface IHosGraphConstructor {
    createGraph: (
        divObject: HTMLElement,
        dataObject: IGraphData,
        options: IGraphOptions,
        clickCallback?: TGraphClickHandler,
        violations?: IViolation[],
        breaks?: IBreakPeriod[]
    ) => void;
    removeHandlers: () => void;
    graphState: (newDataObject: IGraphParams) => IGraphParams;
    //For tests
    getStatusCounters: () => number[];
    getTotalHours: () => number;
    getHoursAndMinutesFromStatusCounters: () => number[][];
}

// Standard statuses in order to be displayed on the graph
export const STANDARD_STATUS_LOG_TYPES_GRAPH_ORDER: THosStatus[] = ["OFF", "SB", "D", "ON"];

// EWD statuses in order to be displayed on the graph
export const AUSTRALIAN_LOG_TYPES_GRAPH_ORDER: THosStatus[] = ["Rest", "Work"];

export const ALL_LOG_TYPES_GRAPH_ORDER: THosStatus[] = [...STANDARD_STATUS_LOG_TYPES_GRAPH_ORDER, ...AUSTRALIAN_LOG_TYPES_GRAPH_ORDER];

// position (in % to total height) for standart statuses
interface IElementYPosition {
    timesRowTopPercent: number;
    gridTopPercent: number;
}

interface IRenderSettings {
    rowStatuses: THosStatus[];
    allowedStatuses: THosStatus[];
    yPositions: IElementYPosition;
}

export const defaultRenderSettings: IRenderSettings = {
    rowStatuses: [...STANDARD_STATUS_LOG_TYPES_GRAPH_ORDER],
    allowedStatuses: [...STATUS_LOG_TYPES],
    yPositions: {
        timesRowTopPercent: 5,
        gridTopPercent: 7
    }
};

const getHeightMultiplier = (statuses: TDutyStatusLogStatus[]) => defaultRenderSettings.rowStatuses.length / statuses.length;

const getYPositions = (rowStatuses: TDutyStatusLogStatus[]): IElementYPosition => {
    let heightMultiplier = getHeightMultiplier(rowStatuses);
    let defaultYPositions = defaultRenderSettings.yPositions;
    return {
        timesRowTopPercent: defaultYPositions.timesRowTopPercent * heightMultiplier,
        gridTopPercent: defaultYPositions.gridTopPercent * heightMultiplier
    };
};

export const getGraphHeightByWidth = (width: number, rowStatuses: TDutyStatusLogStatus[], offsetRequired = false) => {
    let baseCalc = (width * 0.2) / getHeightMultiplier(rowStatuses);
    return offsetRequired ? baseCalc + legendOffset : baseCalc;
};

export interface IBreakPeriod {
    fromDate: Date;
    toDate: Date;
}

interface IStyles {
    fillColor?: string;
    fillPattern?: string;
    strokeColor?: string;
    strokeWidth?: number;
    fillOpacity?: number;
    mask?: string;
    horizontalCornerRadius?: number;
    verticalCornerRadius?: number;
}

interface ILegendLabel {
    labels: string[];
    style: IStyles;
    id: string;
}

type TLegendLabelTree = string | ILegendLabel | TLegendLabelTree[];

interface IGraphColors {
    innerLineColor: string;
    outerLineColor: string;
    fillColor: string;
    approvedColor: string;
    modifiedColor: string;
}

interface IPeriod {
    fromDate: Date;
    toDate: Date | null;
}

export interface IRenderPeriod extends IPeriod {
    toDate: Date;
    index: number;
    logStatus: TDutyStatusLogStatus;
    isVerified?: boolean;
    isEdited?: boolean;
    logId: string;
    isSituation?: boolean;
}

interface IDimensions {
    width: number;
    fontSize: number;
    rowInterval: number;
    columnInterval: number;
    columns: number;
    rows: number;
    height: number;
    rectSide: number;
    labelRows: number;
    rowGap: number;
    spaceBetween: number;
    leftMargin: number;
    labelsInterval: number;
}

export interface IGraphLog {
    id: string;
    dateTime: string;
    status: TDutyStatusLogStatus;
    verifyDateTime?: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _isEdited?: boolean;
}

export interface IGraphData {
    data: IGraphLog[];
    fromDate: Date;
    toDate: Date;
    renderUntil?: Date;
    driverTimeZone?: string;
}

export interface IGraphOptions {
    statuses?: TDutyStatusLogStatus[];
    allStatuses?: TDutyStatusLogStatus[];
    driver?: TDrawDriver;
    color?: IGraphColors;
    xStartPercentage?: number;
    skipHours?: number;
    fontBold?: boolean;
    fontScale?: number;
    includeVerified?: boolean;
    includeEdited?: boolean;
    includeViolations?: boolean;
    includeBreaks?: boolean;
    includePcYm?: boolean;
    includeWorkExemption?: boolean;
    isEwd?: boolean;
    getLocalDateTime?: (date: Date | string, timeZoneId?: string) => Date;
    shortTimeFormat?: string;
    parseDateToString?: (date: Date, format: string) => string;
    translate?: (phrase: string) => string;
    showLegend?: boolean;
    containerWidth?: number; // Setting this option increases the speed of rendering because we don't need to calculate it for each graph on the page
    getBarDescriptorId?: (id: string, isPlaceholder: boolean) => string; // method that lets get the id attribute of the corresponding element (used for populating aria-* attributes)
    customGraphColors?: (status: string) => string;
}

interface IGraphParams {
    data: IGraphData;
    violations: IViolation[];
    breaks: IBreakPeriod[];
    clickHandler?: TGraphClickHandler;
    options: IGraphOptions;
}

const DEFAULT_DRIVER: TDrawDriver = "svg";
const DEFAULT_COLOR_SCHEME: IGraphColors = {
    fillColor: "white",
    outerLineColor: "black",
    innerLineColor: "#C4C4C4",
    approvedColor: "green",
    modifiedColor: "yellow"
};
const DEFAULT_X_START_PERCENTAGE = 5;

// Need use func to prevent overriding statuses arrays
const getDefaultOptions = () => ({
    statuses: [...defaultRenderSettings.rowStatuses],
    allStatuses: [...defaultRenderSettings.allowedStatuses],
    isEwd: false,
    driver: DEFAULT_DRIVER,
    color: DEFAULT_COLOR_SCHEME,
    xStartPercentage: DEFAULT_X_START_PERCENTAGE,
    showLegend: true,
    getLocalDateTime: (dateTime: string | Date) => (typeof dateTime === "string" ? new Date(dateTime) : dateTime)
});
const GRAPH_BAR_CLASS_NAME = "hos-graph-bar";
const GRAPH_SITUATIONAL_DRIVING_HIGHLIGHT_CLASS_NAME = "hos-graph-situational-driving-highlight";
const DESCRIPTOR_ID_PREFIX = "hosGraphDescriptor";
const DESCRIPTOR_CLASS_NAME = "hos-graph-desc";
const MAIN_GROUP_ID = "hos-graph-grid";
const MAIN_CONTENT_GROUP_ID = "hos-graph-content";
const CONNECTING_LINES_GROUP_ID = "hos-graph-connecting-lines";
const STATUS_GROUP_GRID_PREFIX = "grid-hos-graph-status";
const STATUS_GROUP_BAR_PREFIX = "bar-hos-graph-status";
const HOUR_PATTERN_ID = "hour-pattern";

export const generateId = () => Math.floor(Math.random() * 1000000);

const defaultDateToString = (date: Date) => {
    let hour = date.getHours();
    let postfix = hour >= 12 ? "PM" : "AM";
    return `${hour % 12 || 12}${postfix}`;
};

class SVGDrawDriver implements IDrawDriver<SVGElement> {
    private readonly styleNames: { [key in keyof IStyles]: string } = {
        fillColor: "fill",
        fillPattern: "fill",
        strokeColor: "stroke",
        strokeWidth: "stroke-width",
        fillOpacity: "fill-opacity",
        mask: "mask",
        horizontalCornerRadius: "rx",
        verticalCornerRadius: "ry"
    };
    private svgObject!: SVGElement;
    private width!: number;
    private height!: number;
    private readonly id = `graph_${generateId()}`;
    private readonly listeners: { target: Element; eventType: string; listener: EventListener }[] = [];

    private getMaskDefinition(): Node {
        let defs = document.createElementNS(SVGDrawDriver.svgURI, "defs"),
            pattern = document.createElementNS(SVGDrawDriver.svgURI, "pattern"),
            patternRect = document.createElementNS(SVGDrawDriver.svgURI, "rect"),
            mask = document.createElementNS(SVGDrawDriver.svgURI, "mask"),
            maskRect = document.createElementNS(SVGDrawDriver.svgURI, "rect");

        patternRect.setAttribute("width", "4");
        patternRect.setAttribute("height", "8");
        patternRect.setAttribute("transform", "translate(0,0)");
        patternRect.setAttribute("fill", "white");

        pattern.setAttribute("width", "8");
        pattern.setAttribute("height", "8");
        pattern.setAttribute("id", "pattern-stripe");
        pattern.setAttribute("patternUnits", "userSpaceOnUse");
        pattern.setAttribute("patternTransform", "rotate(45)");

        mask.setAttribute("id", "mask-stripe");

        maskRect.setAttribute("width", "100%");
        maskRect.setAttribute("height", "100%");
        maskRect.setAttribute("x", "0");
        maskRect.setAttribute("y", "0");
        maskRect.setAttribute("fill", "url(#pattern-stripe)");

        pattern.appendChild(patternRect);
        mask.appendChild(maskRect);

        defs.setAttribute("id", "definitions");
        defs.appendChild(pattern);
        defs.appendChild(mask);

        return defs;
    }

    private addListener(id: string, eventType: string, target: Element, listener: (id: string) => void) {
        let eventListener = (e: Event) => {
            if (e.type === "click" || (e.type === "keyup" && (<KeyboardEvent>e).keyCode === 13)) {
                listener(id);
            }
        };
        this.listeners.push({
            eventType,
            target,
            listener: eventListener
        });
        target.addEventListener(eventType, eventListener, false);
    }

    private getDrawContainer(containerId: string) {
        return (containerId && this.svgObject.querySelector(`#${containerId}`)) || this.svgObject;
    }

    private isStyleName(key: string): key is keyof IStyles {
        return Object.prototype.hasOwnProperty.call(this.styleNames, key);
    }

    createCanvas(width: number, height: number) {
        this.width = width;
        this.height = height;
        this.svgObject = document.createElementNS(SVGDrawDriver.svgURI, "svg") as SVGElement;
        this.svgObject.setAttribute("class", "svgGraph");
        this.svgObject.setAttribute("width", "100%");
        this.svgObject.setAttribute("height", "100%");
        this.svgObject.setAttribute("viewBox", `0 0 ${width} ${height}`);
        this.svgObject.setAttribute("preserveAspectRatio", "none");
        this.svgObject.setAttribute("role", "group");
        this.svgObject.setAttribute("tabindex", "0");
        this.svgObject.appendChild(this.getMaskDefinition());
        this.svgObject.setAttribute("id", this.id.toString());
    }

    getCanvas() {
        return this.svgObject;
    }

    setCanvas(svgObject: SVGElement) {
        let viewBoxDims = (svgObject.getAttribute("viewBox") || "0 0 0 0").split(" ");
        this.width = +viewBoxDims[2];
        this.height = +viewBoxDims[3];
        this.svgObject = svgObject;
        this.svgObject.setAttribute("id", this.id.toString());
    }

    setDescriptorId(graphDescriptorId: string) {
        let mainGroup = this.getDrawContainer(MAIN_GROUP_ID);
        mainGroup.setAttribute("aria-labelledby", graphDescriptorId);
        mainGroup.setAttribute("role", "figure");
    }

    getWidth() {
        return this.width;
    }

    getHeight() {
        return this.height;
    }

    rect(
        x: number,
        y: number,
        width: number,
        height: number,
        style: IStyles,
        id = "",
        className = "",
        clickHandler: TGraphClickHandler | null = null,
        labelId?: string,
        containerId?: string
    ) {
        let rect = document.createElementNS(SVGDrawDriver.svgURI, "rect"),
            styleString = Object.keys(style)
                .map(key => {
                    if (!this.isStyleName(key)) {
                        return null;
                    }
                    let styleName = this.styleNames[key],
                        styleValue = style[key];
                    if (key === "fillPattern") {
                        styleValue = `url(#${styleValue})`;
                    }

                    return styleName ? `${styleName}: ${styleValue};` : null;
                })
                .filter((value): value is string => value !== null)
                .join("");

        rect.setAttribute("x", `${x}`);
        rect.setAttribute("y", `${y}`);
        rect.setAttribute("width", `${width}`);
        rect.setAttribute("height", `${height}`);
        rect.setAttribute("style", styleString);
        className && rect.setAttribute("class", className);
        if (id && clickHandler) {
            let link = document.createElementNS(SVGDrawDriver.svgURI, "a");
            link.appendChild(rect);
            rect = link;
            rect.setAttribute("xlink:href", "#");
            rect.setAttribute("role", "link");
            if (labelId) {
                rect.setAttribute("aria-describedby", labelId);
            } else {
                // Provide accessible name when labelId is not available
                rect.setAttribute("aria-label", `HOS graph element ${id}`);
            }
            this.addListener(id, "click", rect, clickHandler);
            this.addListener(id, "keyup", rect, clickHandler);
        } else {
            rect.setAttribute("aria-hidden", "true");
        }
        id && rect.setAttribute("id", id);
        let targetContainer = containerId ? this.getDrawContainer(containerId) : this.svgObject;
        targetContainer.appendChild(rect);
    }

    line(x1: number, y1: number, x2: number, y2: number, color: string, width = 1.5, containerId?: string) {
        let line = document.createElementNS("http://www.w3.org/2000/svg", "line");

        line.setAttribute("x1", `${x1}`);
        line.setAttribute("y1", `${y1}`);
        line.setAttribute("x2", `${x2}`);
        line.setAttribute("y2", `${y2}`);
        line.setAttribute("stroke", color);
        line.setAttribute("stroke-width", `${width}`);
        line.setAttribute("aria-hidden", "true");
        let targetContainer = containerId ? this.getDrawContainer(containerId) : this.svgObject;
        targetContainer.appendChild(line);
    }

    text(
        x: number,
        y: number,
        text: string,
        fontSize: number,
        fontWeight: TFontWeight = "normal",
        className = "",
        color = "black",
        id?: string,
        containerId?: string
    ) {
        let textElement = document.createElementNS(SVGDrawDriver.svgURI, "text") as SVGTextElement;
        let styleString = `font-size: ${fontSize}px; font-weight: ${fontWeight}; fill: ${color};`;

        textElement.setAttribute("style", styleString);
        textElement.setAttribute("x", `${x}`);
        textElement.setAttribute("y", `${y}`);
        textElement.setAttribute("aria-hidden", "true");
        textElement.textContent = text;
        id && textElement.setAttribute("id", id);
        className && textElement.setAttribute("class", className);

        let targetContainer = containerId ? this.getDrawContainer(containerId) : this.svgObject;
        targetContainer.appendChild(textElement);
    }

    group(id: string, attributes: IStringHash = {}, containerId?: string) {
        let groupElement = document.createElementNS(SVGDrawDriver.svgURI, "g") as SVGTextElement;

        groupElement.setAttribute("id", id);
        for (let attr in attributes) {
            groupElement.setAttribute(attr, attributes[attr]);
        }

        let targetContainer = containerId ? this.getDrawContainer(containerId) : this.svgObject;
        targetContainer.appendChild(groupElement);
    }

    pattern(x = 0, y = 0, width = 0, height = 0, id = "", containerId?: string) {
        let pattern = document.createElementNS(SVGDrawDriver.svgURI, "pattern");
        pattern.setAttribute("x", `${x}`);
        pattern.setAttribute("y", `${y}`);
        pattern.setAttribute("width", `${width}`);
        pattern.setAttribute("height", `${height}`);
        pattern.setAttribute("patternUnits", "userSpaceOnUse");
        pattern.setAttribute("id", id);
        let targetContainer = containerId ? this.getDrawContainer(containerId) : this.svgObject;
        targetContainer.appendChild(pattern);
    }

    removeListeners() {
        this.listeners &&
            this.listeners.forEach(({ target, eventType, listener }) => {
                target.removeEventListener(eventType, listener, false);
            });
    }

    static svgURI = "http://www.w3.org/2000/svg";
}

class CanvasDrawDriver implements IDrawDriver<HTMLCanvasElement> {
    private canvas!: HTMLCanvasElement;
    private ctx!: CanvasRenderingContext2D;
    private readonly margin = 0;
    private width!: number;
    private height!: number;
    private readonly patterns: ITypedHash<CanvasPattern> = {};
    private readonly clickHandlers: { id: string; x1: number; y1: number; x2: number; y2: number; handler: (id: string) => void }[] = [];

    private readonly onClick = (e: MouseEvent) => {
        this.clickHandlers.forEach(clickHandler => {
            let pos = this.canvas.getBoundingClientRect();
            if (
                e.x >= clickHandler.x1 + pos.left &&
                e.y >= clickHandler.y1 + pos.top &&
                e.x <= clickHandler.x2 + pos.left &&
                e.y <= clickHandler.y2 + pos.top
            ) {
                clickHandler.handler(clickHandler.id);
            }
        });
    };

    createCanvas(width: number, height: number) {
        this.width = width;
        this.height = height;
        this.canvas = document.createElement("canvas");
        this.ctx = this.canvas.getContext("2d") as CanvasRenderingContext2D;
        this.canvas.setAttribute("width", `${width + this.margin}px`);
        this.canvas.setAttribute("height", `${height + this.margin}px`);
        this.canvas.setAttribute("tabindex", "0");
        this.canvas.addEventListener("click", this.onClick, false);
    }

    getCanvas() {
        return this.canvas;
    }

    setCanvas(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        this.ctx = this.canvas.getContext("2d") as CanvasRenderingContext2D;
        this.width = parseFloat(this.canvas.getAttribute("width") || "0");
        this.width = parseFloat(this.canvas.getAttribute("height") || "0");
    }

    setLabel(label: string) {
        this.canvas.setAttribute("aria-label", label);
    }

    setDescriptorId(graphDescriptorId: string) {
        this.canvas.setAttribute("aria-labelledby", graphDescriptorId);
    }

    getWidth() {
        return this.width - this.margin * 2;
    }

    getHeight() {
        return this.height - this.margin * 2;
    }

    rect(
        x: number,
        y: number,
        width: number,
        height: number,
        style: IStyles,
        id?: string,
        _className?: string,
        clickHandler?: TGraphClickHandler | null,
        _labelId?: string,
        _containerId?: string
    ) {
        this.ctx.strokeStyle = style.strokeColor || "black";
        this.ctx.lineWidth = style && style.strokeWidth ? style.strokeWidth / 2 : 1;

        if (style.fillColor) {
            this.ctx.globalAlpha = style.fillOpacity || 1;
            this.ctx.fillStyle = style.fillColor;
            this.ctx.fillRect(x + this.margin, y + this.margin, width, height);
        }

        if (style.fillPattern) {
            this.ctx.globalAlpha = style.fillOpacity || 1;
            this.ctx.fillStyle = this.patterns[style.fillPattern];
            this.ctx.translate(x + this.margin, y + this.margin);
            this.ctx.fillRect(0, 0, width, height);
            this.ctx.translate(-x - this.margin, -y - this.margin);
        }

        this.ctx.globalAlpha = 1;
        style.strokeWidth && this.ctx.strokeRect(x + this.margin, y + this.margin, width, height);
        if (id && clickHandler) {
            this.clickHandlers.push({
                id,
                x1: x + this.margin,
                y1: y + this.margin,
                x2: x + this.margin + width,
                y2: y + this.margin + height,
                handler: clickHandler
            });
        }
    }

    line(x1: number, y1: number, x2: number, y2: number, color = "black", width = 1, _containerId?: string) {
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = width / 2;
        this.ctx.beginPath();
        this.ctx.moveTo(x1 + this.margin, y1 + this.margin);
        this.ctx.lineTo(x2 + this.margin, y2 + this.margin);
        this.ctx.stroke();
    }

    text(
        x: number,
        y: number,
        text: string,
        fontSize: number,
        fontWeight: TFontWeight = "normal",
        _className = "",
        color = "black",
        _id?: string,
        _containerId?: string
    ) {
        this.ctx.fillStyle = color;
        this.ctx.font = `${fontWeight} ${fontSize}px Sans-Serif`;
        this.ctx.fillText(text, x + this.margin, y + this.margin);
    }

    pattern(
        _x?: number,
        _y?: number,
        _width?: number,
        _height?: number,
        id = "",
        _containerId?: string,
        pattern?: CanvasImageSource
    ) {
        if (!pattern) {
            return;
        }
        let patternCanvas = this.ctx.createPattern(pattern, "repeat") as CanvasPattern;
        this.patterns[id] = patternCanvas;
    }

    removeListeners() {
        this.canvas.removeEventListener("click", this.onClick, false);
    }
}

class GraphGridCache<T extends TView> {
    private grid: T | null = null;

    private static instance: GraphGridCache<TView>;
    private static containerWidth: number;
    private static driver: "svg" | "canvas";
    private static colorScheme: IGraphColors;
    private static xStartPercentage: number;
    private static skipHours?: number;
    private static isBold: boolean;
    private static shortTimeFormat?: string;
    private static colsHeaders: string;
    private static rowsQty: number;
    private static legendHeight: number;

    private static isCacheMatch(newContainer: HTMLElement, newOptions: IGraphOptions, colsHeaders: string[], rowsQty: number, legendHeight: number) {
        let newContainerWidth = newOptions.containerWidth || newContainer.clientWidth;
        let containerWidthMatch = GraphGridCache.containerWidth === newContainerWidth;
        let driverMatch = GraphGridCache.driver === (newOptions.driver || DEFAULT_DRIVER);
        let newColorScheme = newOptions.color || DEFAULT_COLOR_SCHEME;
        let colorsMatch =
            GraphGridCache.colorScheme.innerLineColor === newColorScheme.innerLineColor &&
            GraphGridCache.colorScheme.outerLineColor === newColorScheme.outerLineColor;
        let newXStartPercentage = newOptions.xStartPercentage || DEFAULT_X_START_PERCENTAGE;
        let xStartPercentageMatch = GraphGridCache.xStartPercentage === newXStartPercentage;
        let skipHoursMatch = GraphGridCache.skipHours === newOptions.skipHours;
        let boldMatch = GraphGridCache.isBold === newOptions.fontBold;
        let timeFormatMatch = GraphGridCache.shortTimeFormat === newOptions.shortTimeFormat;
        let colsHeadersMatch = GraphGridCache.colsHeaders === colsHeaders.join();
        let rowsQtyMatch = GraphGridCache.rowsQty === rowsQty;
        let legendHeightMatch = GraphGridCache.legendHeight === legendHeight;
        return (
            containerWidthMatch &&
            driverMatch &&
            colorsMatch &&
            skipHoursMatch &&
            xStartPercentageMatch &&
            boldMatch &&
            timeFormatMatch &&
            colsHeadersMatch &&
            rowsQtyMatch &&
            legendHeightMatch
        );
    }

    private constructor(container: HTMLElement, options: IGraphOptions, rowsQty: number, colsHeaders: string[], legendHeight: number) {
        GraphGridCache.containerWidth = options.containerWidth || container.clientWidth;
        GraphGridCache.driver = options.driver || DEFAULT_DRIVER;
        GraphGridCache.colorScheme = options.color || DEFAULT_COLOR_SCHEME;
        GraphGridCache.xStartPercentage = options.xStartPercentage || DEFAULT_X_START_PERCENTAGE;
        GraphGridCache.skipHours = options.skipHours;
        GraphGridCache.isBold = !!options.fontBold;
        GraphGridCache.shortTimeFormat = options.shortTimeFormat;
        GraphGridCache.rowsQty = rowsQty;
        GraphGridCache.colsHeaders = colsHeaders.join();
        GraphGridCache.legendHeight = legendHeight;
    }

    set(grid: T) {
        this.grid = grid.cloneNode(true) as T;
    }

    get() {
        return this.grid && (this.grid.cloneNode(true) as T);
    }

    static getInstance<U extends SVGElement | HTMLCanvasElement>(
        container: HTMLElement,
        options: IGraphOptions,
        colsHeaders: string[],
        rowsQty: number,
        legendHeight: number
    ) {
        let instance =
            GraphGridCache.instance && GraphGridCache.isCacheMatch(container, options, colsHeaders, rowsQty, legendHeight)
                ? GraphGridCache.instance
                : new GraphGridCache<U>(container, options, rowsQty, colsHeaders, legendHeight);
        GraphGridCache.instance = instance;
        return instance;
    }
}

interface IDuration {
    hours?: number;
    minutes: number;
    seconds?: number;
}

class GraphDescriptor {
    private el!: HTMLElement;
    private list!: HTMLElement;

    private createContainer() {
        this.el = document.createElement("DIV");
        this.el.setAttribute("id", this.id);
        this.el.setAttribute("class", DESCRIPTOR_CLASS_NAME);
        this.el.style.display = "none";
        let label = document.createElement("SPAN");
        label.setAttribute("role", "title");
        label.textContent = this.translate("Duty Status Graph and HOS Logs");
        this.list = document.createElement("UL");
        this.el.appendChild(label);
        this.el.appendChild(this.list);
    }

    private addRow(content: string) {
        let el = document.createElement("LI");
        el.setAttribute("class", `${DESCRIPTOR_CLASS_NAME}__item`);
        el.textContent = content;
        this.list.appendChild(el);
    }

    private translateTmpl(strings: TemplateStringsArray, ...values: unknown[]) {
        let strToTranslate = strings.reduce((res, subStr, i) => res + `{${i - 1}}` + subStr.replace(/\s+/gm, " "));
        return this.translate(strToTranslate.replace(/(\/r)|(\/n)/gm, "")) // removing new lines
            .replace(/\{(\d)\}/gm, (_match: string, index: string) => String(values[Number(index)])); // replacing placeholders
    }

    constructor(private readonly translate: (key: string) => string) {
        this.id = `${DESCRIPTOR_ID_PREFIX}_${generateId()}`;
        this.createContainer();
    }

    readonly id: string;

    addStatusRow(index: number, duration: IDuration, statuses: TDutyStatusLogStatus[]) {
        let hoursStr = duration.hours !== undefined ? `${duration.hours} ${this.translate("hours")}` : "";
        let minutesStr = duration.minutes !== undefined ? `${duration.minutes} ${this.translate("{minutes}m").replace("{minutes}", "")}` : "";
        let secondsStr = duration.seconds !== undefined ? `${duration.seconds} ${this.translate("seconds")}` : "";
        this.addRow(this.translateTmpl`Total ${statuses[index]} duration: ${hoursStr} ${minutesStr} ${secondsStr}`);
    }

    pasteInto(container: HTMLElement) {
        container.appendChild(this.el);
    }
}

const DEFAULT_TIME_FORMAT = "htt";
const legendOffset = 30;

export function hosGraphConstructor(): IHosGraphConstructor {
    let containerDivObject: HTMLElement,
        currentDataObject: IGraphParams,
        statuses: TDutyStatusLogStatus[],
        allStatuses: TDutyStatusLogStatus[],
        statusCounters = [0, 0, 0, 0, 0], //TODO: Fix terrible architecture. Can't get those parameters out of render otherwise
        localizeTime = (function () {
            return (fromDate: Date, toDate: Date, shortTimeFormat: string, parseDateToString: (date: Date, format: string) => string) => {
                let format = shortTimeFormat || DEFAULT_TIME_FORMAT;

                // Handle DST: add hour or remove hour when DST happen
                let startTimeMs = fromDate.getTime();
                let endTimeMs = toDate.getTime();
                let toLocalDateTime =
                    currentDataObject.options.getLocalDateTime || ((time: string | Date) => (typeof time === "string" ? new Date(time) : time));
                let driverTimeZone = currentDataObject.data.driverTimeZone;

                let currentTimeMs = startTimeMs;
                let graphTimes: number[] = [];
                while (currentTimeMs <= endTimeMs) {
                    let localTime = toLocalDateTime(new Date(currentTimeMs), driverTimeZone).getTime();
                    graphTimes.push(localTime);
                    currentTimeMs += 3600000;
                }
                if (format.indexOf("tt") > -1) {
                    format = format.replace(":mm ", "");
                }
                let newTimes = graphTimes.map(time => parseDateToString(new Date(time), format));

                return newTimes;
            };
        })(),
        driver: CanvasDrawDriver | SVGDrawDriver,
        getStatusCounters = () => statusCounters,
        getMinutesFromDecimalHours = (decimalHours: number) => {
            return Math.round((decimalHours % 1) * 60 || 0);
        },
        getHoursFromDecimalHours = (decimalHours: number) => {
            return Math.floor(decimalHours);
        },
        getHoursAndMinutesFromStatusCounters = () => {
            let stats = getStatusCounters(),
                totalHours = stats.reduce((a, b) => a + b) / (60 * 60 * 1000),
                result = [
                    [0, 0],
                    [0, 0],
                    [0, 0],
                    [0, 0],
                    [0, 0]
                ],
                getLeftOvers = (hourTotal: number, hoursCountSoFar: number) => {
                    let leftOvers = result.reduce((a, b) => [a[0] + b[0], a[1] + b[1]]);
                    return Math.round((hourTotal - leftOvers[0] - hoursCountSoFar) * 60 - leftOvers[1]);
                };

            for (let i = stats.length - 1; i >= 0; i--) {
                let statsDecimalHours = stats[i] / (60 * 60 * 1000),
                    hours = getHoursFromDecimalHours(statsDecimalHours),
                    minutes = 0;

                if (i > 0) {
                    minutes = getMinutesFromDecimalHours(statsDecimalHours);
                } else if (i === 0) {
                    /**
                     * For the OFF duty record (at index 0), we should just assign the leftover minutes from 24 hours.
                     * Otherwise, rounding minutes causes the total of all statuses to not equal 24 hours.
                     * Start with OFF duty, and if there are no OFF status records at all, continue to traverse
                     * through statuses until the leftovers can be applied
                     */
                    for (let j = 0; j < 5; j++) {
                        if (stats[j] > 0) {
                            result[j][1] += getLeftOvers(totalHours, hours);

                            if (j === 0) {
                                minutes = result[j][1];
                            }
                            break;
                        }
                    }
                }

                result[i] = [hours, minutes];
            }

            // Tidy up and make sure any 60 minutes just add an hour
            for (let i = 0, j = result.length; i < j; i++) {
                if (result[i][1] === 60) {
                    result[i][1] = 0;
                    result[i][0]++;
                }
            }

            return result;
        },
        getDriver = function () {
            driver && driver.removeListeners();
            let driverName = currentDataObject.options.driver || DEFAULT_DRIVER;
            return driverName === "canvas" ? new CanvasDrawDriver() : new SVGDrawDriver();
        },
        /**
         * gets the style/color for the bar in question
         **/
        getBarStyle = function (
            approved = false,
            modified = false,
            violation = false,
            isBreak = false,
            isSituation = false,
            isEwd = false,
            color: IGraphColors
        ): IStyles {
            let style: IStyles = {
                strokeColor: color.outerLineColor,
                strokeWidth: 1
            };

            if (isSituation) {
                style.mask = "url(#mask-stripe)";
            }

            if (violation) {
                style.fillColor = "red";
                style.fillOpacity = 0.5;
            } else if (isBreak) {
                style.fillColor = "#008eff";
                style.fillOpacity = 0.5;
            } else {
                style.fillOpacity = 0.4;
                if (isEwd || approved || modified) {
                    style.fillColor = isEwd || approved ? color.approvedColor : color.modifiedColor;
                } else {
                    style.fillColor = color.outerLineColor;
                }
            }

            return style;
        },
        getStatusColorByRow = (status: TDutyStatusLogStatus) => {
            switch (status) {
                case "OFF":
                case "Rest":
                    return "#777772";
                case "SB":
                    return "#E32943";
                case "D":
                    return "#328812";
                case "ON":
                case "Work":
                    return "#A36B0D";
                default:
                    return "#4376C8";
            }
        },
        getHOSStatusLabel = (status: TDutyStatusLogStatus) => {
            switch (status) {
                case "OFF":
                    return translate("Total Off Duty: {0}");
                case "SB":
                    return translate("Total Sleeper Berth: {0}");
                case "D":
                    return translate("Total Drive: {0}");
                case "ON":
                    return translate("Total On Duty: {0}");
                case "WT":
                    return translate("Total Wait time: {0}");
                case "Work":
                    return translate("Total Work: {0}");
                case "Rest":
                    return translate("Total Rest: {0}");
                default:
                    return `Total ${status} {0}`;
            }
        },
        getGraphTimeStampLabel = (hours: number, minutes: number) => {
            return `${hours}${translate("h")} ${minutes}${translate("{minutes}m").replace("{minutes}", "")}`;
        },
        getTimeStrings = () => {
            return localizeTime(
                currentDataObject.data.fromDate,
                currentDataObject.data.toDate,
                currentDataObject.options.shortTimeFormat || DEFAULT_TIME_FORMAT,
                currentDataObject.options.parseDateToString || defaultDateToString
            );
        },
        translate = (str: string) => {
            return (currentDataObject.options.translate && currentDataObject.options.translate(str)) || `**${str}`;
        },
        render = () => {
            driver = getDriver();
            let isEwd = currentDataObject.options.isEwd;
            let getStatusLogs = () => {
                    return (currentDataObject.data.data || []).filter(log => allStatuses.indexOf(log.status) > -1);
                },
                statusLogs = getStatusLogs();

            // Are any of the logs "WT"?
            if (statusLogs.some(log => log.status === "WT") && statuses.indexOf("WT") === -1) {
                statuses.push("WT");
            }
            let rowLimit = statuses.length;
            let timeStrings = getTimeStrings(),
                hoursToShow = timeStrings.length - 1,
                selectResult = containerDivObject.querySelectorAll("svg.svgGraph"),
                color: IGraphColors = currentDataObject.options.color || DEFAULT_COLOR_SCHEME,
                //gets "time distance" between 2 points in hours
                getTimeSpanInHours = (fromDate: Date, toDate: Date) => {
                    let hourInMilisec = 3600000;
                    return (toDate.getTime() - fromDate.getTime()) / hourInMilisec;
                },
                getFontSize = (tileHeight: number) => {
                    return tileHeight * 0.3;
                },
                renderSum = function (textColor: string, graphDescriptor: GraphDescriptor) {
                    let hoursAndMinutes = getHoursAndMinutesFromStatusCounters();
                    let getXPositionForGraphLabel = (tableStartX: number, hoursToShowXRow: number, tileWidth: number, minWidth: number) => {
                        return tableStartX + hoursToShowXRow * tileWidth + minWidth + tileWidth * 0.13;
                    };
                    let getYPositionForGraphLabel = (tableStartY: number, rowsCount: number, tileHeight: number) => {
                        return tableStartY + rowsCount * tileHeight + tileHeight * 0.6;
                    };

                    fontSize = getFontSize(tileHeight);

                    for (let rowsCount = 0; rowsCount < rowLimit; rowsCount++) {
                        let containerId = `${STATUS_GROUP_BAR_PREFIX}_${rowsCount}`;

                        let x = getXPositionForGraphLabel(tableStartX, hoursToShow, tileWidth, minWidth);
                        let y = getYPositionForGraphLabel(tableStartY, rowsCount, tileHeight);
                        let text = getGraphTimeStampLabel(hoursAndMinutes[rowsCount][0], hoursAndMinutes[rowsCount][1]);
                        driver.text(x, y, text, fontSize, "bold", undefined, textColor, undefined, containerId);

                        graphDescriptor.addStatusRow(
                            rowsCount,
                            { hours: hoursAndMinutes[rowsCount][0], minutes: hoursAndMinutes[rowsCount][1] },
                            statuses
                        );
                    }

                    return [
                        getXPositionForGraphLabel(tableStartX, hoursToShow, tileWidth, minWidth),
                        fontSize,
                        tableStartY + (rowLimit - 1) * tileHeight + tileHeight
                    ];
                },
                addAriaLabelForGraph = () => {
                    let hoursAndMinutes = getHoursAndMinutesFromStatusCounters();
                    if (hoursAndMinutes && statuses) {
                        let ariaLabelText = translate("HOS Graph showing:");
                        for (let rowsCount = 0; rowsCount < rowLimit; rowsCount++) {
                            let [hours, minutes] = hoursAndMinutes[rowsCount];
                            ariaLabelText += ` ${getHOSStatusLabel(statuses[rowsCount]).replace("{0}", getGraphTimeStampLabel(hours, minutes))},`;
                        }
                        let totalHours = getTotalHours();
                        ariaLabelText += ` ${translate("Grand total: {0}").replace(
                            "{0}",
                            getGraphTimeStampLabel(getHoursFromDecimalHours(totalHours), getMinutesFromDecimalHours(totalHours))
                        )}`;
                        driver.getCanvas().setAttribute("aria-label", ariaLabelText);
                    }
                },
                isAfter = (a: Date, b: Date) => a.getTime() - b.getTime() > 0,
                isBefore = (a: Date, b: Date) => a.getTime() - b.getTime() < 0,
                getIntersection = (period1: IPeriod, period2: IPeriod): IPeriod | null => {
                    if (
                        (period2.toDate && isBefore(period2.toDate, period1.fromDate)) ||
                        (period1.toDate && isAfter(period2.fromDate, period1.toDate))
                    ) {
                        return null;
                    }

                    let start = isAfter(period1.fromDate, period2.fromDate) ? period1.fromDate : period2.fromDate;
                    let end =
                        period1.toDate && period2.toDate
                            ? isBefore(period1.toDate, period2.toDate)
                                ? period1.toDate
                                : period2.toDate
                            : period1.toDate || period2.toDate || new Date();

                    return {
                        fromDate: new Date(start.getTime()),
                        toDate: new Date(end.getTime())
                    };
                },
                addBreakBar = function (
                    logStart: Date,
                    logEnd: Date,
                    logTypeIndex: number,
                    logId: string | undefined,
                    breaks: IBreakPeriod[]
                ) {
                    let barStart = logStart;
                    let int: IPeriod | null = null;

                    if (breaks.length === 0) {
                        return;
                    }

                    do {
                        let violation = breaks[0] as IPeriod;

                        // fast-forward violations which ended before log start date
                        while (violation && violation.toDate && isBefore(violation.toDate, barStart)) {
                            breaks.shift();
                            violation = breaks[0] as IPeriod;
                        }

                        if (!violation) {
                            continue;
                        }

                        int = getIntersection(
                            {
                                fromDate: barStart,
                                toDate: logEnd
                            },
                            {
                                fromDate: violation.fromDate,
                                toDate: violation.toDate
                            }
                        );

                        if (int && int.toDate) {
                            if (int.toDate !== logStart && statuses[logTypeIndex] === "OFF") {
                                if (isAfter(int.toDate, int.fromDate)) {
                                    buildBarFromData(int.fromDate, int.toDate, logTypeIndex, false, false, false, true, logId, false, "break-bar");
                                }
                                barStart = int.toDate;
                            }

                            if (violation.toDate && !isAfter(violation.toDate, int.toDate)) {
                                breaks.shift();
                            } else {
                                break;
                            }
                        }
                    } while (breaks.length > 0 && int);
                },
                addViolationBar = function (
                    logStart: Date,
                    logEnd: Date,
                    index: number,
                    logStatus: TDutyStatusLogStatus,
                    logId: string | undefined,
                    violationsProcessedList: IViolation[]
                ) {
                    let barStart = logStart;
                    let int: IPeriod | null = null;
                    let allowedViolationStatuses = ["D", "Work", "WorkExemption"];

                    if (violationsProcessedList.length === 0) {
                        return;
                    }

                    do {
                        let violation = violationsProcessedList[0] as IPeriod;

                        // fast-forward violations which ended before log start date
                        while (violation && violation.toDate && isBefore(violation.toDate, barStart)) {
                            violationsProcessedList.shift();
                            violation = violationsProcessedList[0] as IPeriod;
                        }

                        if (!violation) {
                            continue;
                        }

                        int = getIntersection(
                            {
                                fromDate: barStart,
                                toDate: logEnd
                            },
                            {
                                fromDate: violation.fromDate,
                                toDate: violation.toDate
                            }
                        );

                        if (int && int.toDate) {
                            if (int.toDate !== logStart && allowedViolationStatuses.indexOf(logStatus) > -1) {
                                if (isAfter(int.toDate, int.fromDate)) {
                                    buildBarFromData(int.fromDate, int.toDate, index, false, false, true, false, logId, false, "violation-bar");
                                }
                                barStart = int.toDate;
                            }

                            if (violation.toDate && !isAfter(violation.toDate, int.toDate)) {
                                violationsProcessedList.shift();
                            } else {
                                break;
                            }
                        }
                    } while (violationsProcessedList.length > 0 && int);
                },
                addBar = function (
                    startX: number,
                    startY: number,
                    width: number,
                    height: number,
                    style: IStyles,
                    id: string | undefined,
                    className: string,
                    groupId?: string
                ) {
                    let labelId = id && currentDataObject.options.getBarDescriptorId && currentDataObject.options.getBarDescriptorId(id, false);
                    driver.rect(startX, startY, width, height, style, id, className, currentDataObject.clickHandler || null, labelId, groupId);
                },
                getLegendLabels = function (colors: IGraphColors): ILegendLabel[] {
                    let includeVerified = currentDataObject.options.includeVerified;
                    let includeEdited = currentDataObject.options.includeEdited;
                    let includePcYm = currentDataObject.options.includePcYm;
                    let includeViolations = currentDataObject.options.includeViolations;
                    let includeBreaks = currentDataObject.options.includeBreaks;
                    let includeWorkExemption = currentDataObject.options.includeWorkExemption;

                    let localizedLabels: ILegendLabel[] = [];

                    if (includeVerified) {
                        localizedLabels.push(
                            {
                                labels: [translate("Certified")],
                                style: getBarStyle(true, false, false, false, false, isEwd, colors),
                                id: "certified"
                            },
                            {
                                labels: [translate("Uncertified")],
                                style: getBarStyle(false, false, false, false, false, isEwd, colors),
                                id: "uncertified"
                            }
                        );
                    }

                    if (includeEdited) {
                        localizedLabels.push({
                            labels: [translate("Edited")],
                            style: getBarStyle(false, true, false, false, false, isEwd, colors),
                            id: "edited"
                        });
                    }

                    if (includePcYm) {
                        localizedLabels.push(
                            {
                                labels: [translate("Personal Conveyance")],
                                style: getBarStyle(false, false, false, false, true, isEwd, colors),
                                id: "pc"
                            },
                            {
                                labels: [translate("Yard move")],
                                style: getBarStyle(false, false, false, false, true, isEwd, colors),
                                id: "ym"
                            }
                        );
                    }

                    if (includeViolations) {
                        localizedLabels.push({
                            labels: [translate("Violation")],
                            style: getBarStyle(false, false, true, false, false, isEwd, colors),
                            id: "violation"
                        });
                    }

                    if (includeBreaks) {
                        localizedLabels.push({
                            labels: [translate("Qualifying break")],
                            style: getBarStyle(false, false, false, true, false, isEwd, colors),
                            id: "q-break"
                        });
                    }

                    if (includeWorkExemption) {
                        localizedLabels.push({
                            labels: [translate("Work Exemption")],
                            style: getBarStyle(false, false, false, false, true, isEwd, colors),
                            id: "work-exemption"
                        });
                    }

                    return localizedLabels;
                },
                tableStartXPercentage = currentDataObject.options.xStartPercentage || DEFAULT_X_START_PERCENTAGE,
                positions = getYPositions(statuses),
                tableStartYPercentage = positions.gridTopPercent,
                fontSizePercentage = currentDataObject.options.fontScale || 4.3,
                fontWeight: TFontWeight = currentDataObject.options.fontBold ? "bold" : "normal",
                barHeightConstant = 0.87,
                buildBarFromData = (
                    barStartTime: Date,
                    barEndDate: Date,
                    index: number,
                    wasVerified?: boolean,
                    wasEdited?: boolean,
                    isInViolation?: boolean,
                    isBreak?: boolean,
                    recordId?: string,
                    isSituation?: boolean,
                    customClasses = ""
                ) => {
                    let startY, height;
                    let className = `${GRAPH_BAR_CLASS_NAME} ${customClasses}`; // the strange height calculation is to make the bar with same gap at top and bottom

                    // if time difference is too small the length might be 0, needs to convert that to at least 1 pixel;
                    let durationInHours = getTimeSpanInHours(barStartTime, barEndDate);
                    let barLength = durationInHours * tileWidth;
                    barLength = barLength > 0 ? barLength : 1;

                    let startX = tableStartX + getTimeSpanInHours(startTimeStamp, barStartTime) * tileWidth;

                    if (isInViolation || isBreak) {
                        startY = tableStartY + tileHeight * index + tileHeight * barHeightConstant;
                        height = tileHeight * (1 - barHeightConstant);
                    } else {
                        startY = tableStartY + tileHeight * index + tileHeight * (1 - barHeightConstant);
                        height = tileHeight * (2 * barHeightConstant - 1);
                    }

                    let style = getBarStyle(wasVerified || false, wasEdited || false, isInViolation, isBreak, isSituation || false, isEwd, color);
                    let groupId = `${STATUS_GROUP_BAR_PREFIX}_${index}`;
                    addBar(startX, startY, barLength, height, style, recordId, className, groupId);
                    return barLength;
                },
                addConnectingLine = (dateTime: Date, prevState: number, nextState: number) => {
                    let lineX = tableStartX + getTimeSpanInHours(startTimeStamp, dateTime) * tileWidth,
                        fromLineY = tableStartY + tileHeight * prevState + tileHeight * (1 - barHeightConstant),
                        toLineY = tableStartY + tileHeight * nextState + tileHeight * (1 - barHeightConstant);

                    driver.line(lineX, fromLineY, lineX, toLineY, color.outerLineColor, 1, CONNECTING_LINES_GROUP_ID);
                };

            let tileWidthPercentage = (100 - tableStartXPercentage - 8) / hoursToShow;
            let tileHeightPercentage = (100 - tableStartYPercentage) / rowLimit;

            // remove all other images from the div
            for (let i = 0; i < selectResult.length; i++) {
                containerDivObject.removeChild(selectResult[i]);
            }

            let legendLabels = getLegendLabels(color);
            let containerWidth = currentDataObject.options.containerWidth || containerDivObject.clientWidth;
            let legendDimensions = currentDataObject.options.showLegend ? computeDimensions(containerWidth, legendLabels, fontSizePercentage) : null;

            // setting containers height
            let legendHeight = legendDimensions ? legendDimensions.height : 0;
            legendHeight += legendOffset;
            containerDivObject.style.height = `${getGraphHeightByWidth(containerWidth, statuses) + legendHeight}px`;

            // create svg object
            let createHourPattern = () => {
                // if canvas driver then create new Canvas for pattern
                let patternDriver = isSvgDrawDriver(driver) ? driver : new CanvasDrawDriver();
                let patternId = `${driver.getCanvas().id}_${HOUR_PATTERN_ID}`;

                if (isSvgDrawDriver(patternDriver)) {
                    patternDriver.pattern(tableStartX + 0.75, tableStartY, tileWidth, tileHeight, patternId, "definitions");
                } else {
                    patternDriver.createCanvas(tileWidth, tileHeight);
                }

                // Hour line
                patternDriver.line(tileWidth - 0.75, 0, tileWidth - 0.75, tileHeight, color.innerLineColor, 1, patternId);

                if (!isSvgDrawDriver(patternDriver)) {
                    let patternCanvas = patternDriver.getCanvas();
                    driver.pattern(undefined, undefined, undefined, undefined, patternId, undefined, patternCanvas);
                }
            };
            let renderGrid = () => {
                let getGraphColors = currentDataObject.options.customGraphColors || getStatusColorByRow;
                if (isSvgDrawDriver(driver)) {
                    driver.group(MAIN_GROUP_ID);
                    driver.group(MAIN_CONTENT_GROUP_ID);
                }
                const TIMES_GROUP = "hos-graph-times";
                isSvgDrawDriver(driver) && driver.group(TIMES_GROUP, undefined, MAIN_GROUP_ID);
                createHourPattern();
                for (let rowsCount = 0; rowsCount < rowLimit; rowsCount++) {
                    let gridContainerId = `${STATUS_GROUP_GRID_PREFIX}_${rowsCount}`;
                    if (isSvgDrawDriver(driver)) {
                        driver.group(gridContainerId, undefined, MAIN_GROUP_ID);
                        driver.group(`${STATUS_GROUP_BAR_PREFIX}_${rowsCount}`, undefined, MAIN_CONTENT_GROUP_ID);
                    }
                    let captionY = positions.timesRowTopPercent * minHeight;
                    let textHelper;
                    let patternId = `${driver.getCanvas().id}_${HOUR_PATTERN_ID}`;
                    driver.rect(
                        tableStartX,
                        tableStartY + rowsCount * tileHeight,
                        tileWidth * hoursToShow,
                        tileHeight,
                        { fillPattern: patternId, strokeColor: color.innerLineColor, strokeWidth: 1 },
                        undefined,
                        "hos-graph-status-row",
                        undefined,
                        undefined,
                        gridContainerId
                    );

                    for (let columnsCount = 0; columnsCount <= hoursToShow; columnsCount++) {
                        if (rowsCount === 0) {
                            // hour caption
                            if (!currentDataObject.options.skipHours || columnsCount % currentDataObject.options.skipHours === 0) {
                                textHelper = timeStrings[columnsCount];
                                driver.text(
                                    tableStartX - fontSize / 2 + tileWidth * columnsCount - 1,
                                    captionY,
                                    textHelper,
                                    fontSize / 1.3,
                                    fontWeight,
                                    "graphLabel",
                                    color.outerLineColor,
                                    undefined,
                                    TIMES_GROUP
                                );
                            }
                        }

                        if (columnsCount === 0) {
                            let statusColor = getGraphColors(statuses[rowsCount]);
                            let rectWidth = tileHeight;
                            let radius = tileHeight * 0.06;
                            let rectHeight = tileHeight * 0.5;
                            let basicStatuses: string[] = STATUS_LOG_TYPES;
                            textHelper = basicStatuses.indexOf(statuses[rowsCount] as string) !== -1
                                ? translate(statuses[rowsCount])
                                : statuses[rowsCount];
                            let rectYStart = tableStartY + (rowsCount + 0.5) * tileHeight - rectHeight / 2;
                            let labelWidth = getLabelLength(textHelper, fontSize.toString(), fontWeight);

                            driver.rect(
                                0,
                                rectYStart,
                                rectWidth,
                                rectHeight,
                                {
                                    fillColor: statusColor,
                                    horizontalCornerRadius: radius,
                                    verticalCornerRadius: radius
                                },
                                undefined,
                                undefined,
                                undefined,
                                undefined,
                                gridContainerId
                            );
                            driver.text(
                                (rectWidth - labelWidth) / 2,
                                rectYStart + rectHeight * 0.75,
                                textHelper,
                                fontSize,
                                fontWeight,
                                "graphLabel graphStatus",
                                "white",
                                undefined,
                                gridContainerId
                            );
                        }
                    }
                    // Total duration rects
                    driver.rect(
                        tableStartX + hoursToShow * tileWidth + minWidth,
                        tableStartY + rowsCount * tileHeight,
                        minWidth * 6 || 0,
                        tileHeight,
                        { fillColor: color.fillColor },
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        gridContainerId
                    );
                }
                if (isSvgDrawDriver(driver)) {
                    driver.group(GRAPH_SITUATIONAL_DRIVING_HIGHLIGHT_CLASS_NAME, undefined, MAIN_CONTENT_GROUP_ID);
                }
                isSvgDrawDriver(driver) && driver.group(CONNECTING_LINES_GROUP_ID, undefined, MAIN_CONTENT_GROUP_ID);
            };
            let calcFontSize = (height: number) => (height > 45 ? height * 0.3 : height * 0.35) || 0;

            let gridCache = GraphGridCache.getInstance(containerDivObject, currentDataObject.options, timeStrings, rowLimit, legendHeight);
            let grid = gridCache.get();
            if (grid) {
                if (isSvgDrawDriver(driver)) {
                    driver.setCanvas(grid as SVGElement);
                } else {
                    driver.setCanvas(grid as HTMLCanvasElement);
                }
            } else {
                driver.createCanvas(containerWidth, containerDivObject.clientHeight);
            }

            // get minimum size units (1%)
            let minHeight = (driver.getHeight() - legendHeight) / 100;
            let minWidth = driver.getWidth() / 100;

            // populate constants with respect to % of the screen real-estate. All constants are % of the screen to use.
            let tableStartX = Math.floor(minWidth * tableStartXPercentage);
            let tableStartY = Math.floor(minHeight * tableStartYPercentage);
            let tileWidth = Math.floor(minWidth * tileWidthPercentage);
            let tileHeight = Math.floor(minHeight * tileHeightPercentage);

            // setup the grid
            let fontSize = calcFontSize(tileHeight);

            let getLabelLength = (label: string, size: string, weight: string) => {
                let elem = document.createElement("span");

                elem.style.fontSize = `${size}px`;
                elem.style.fontWeight = weight;
                elem.style.position = "absolute";
                elem.style.top = "-100px";
                elem.innerHTML = label;
                document.body.appendChild(elem);

                let biggestWidth = elem.clientWidth;
                document.body.removeChild(elem);

                return Math.ceil(biggestWidth);
            };

            if (!grid) {
                renderGrid();
                gridCache.set(driver.getCanvas());
            }

            // render legend on bottom of graph
            legendDimensions && renderLegend(legendDimensions, legendLabels, tableStartY + rowLimit * tileHeight + legendDimensions.rowGap / 2);

            //get the start and end dates, will be needed for calculation of the length of the bars
            let startTimeStamp = new Date(currentDataObject.data.fromDate.toISOString());
            let endDate = new Date(currentDataObject.data.toDate.toISOString());
            let currentTime = new Date();

            if (endDate.getTime() > currentTime.getTime()) {
                // if day is not done
                endDate = currentTime;
            }

            let renderUntil = currentDataObject.data.renderUntil;
            let renderUntilTimestamp = renderUntil instanceof Date && !isNaN(renderUntil.getTime()) ? renderUntil.getTime() : undefined;
            let hasRenderLimit = false;
            if (renderUntilTimestamp !== undefined && renderUntilTimestamp < endDate.getTime()) {
                endDate = new Date(renderUntilTimestamp);
                hasRenderLimit = true;
            }

            let getSortedViolationsCopy = (violations: IViolation[]) => {
                let violationListCopy: IViolation[] = (violations || []).map(v => ({ ...v }));
                violationListCopy.sort((a, b) => {
                    let dateA = a.fromDate;
                    let dateB = b.fromDate;
                    return dateA.getTime() - dateB.getTime();
                });
                return violationListCopy;
            };
            let violationListCopy = getSortedViolationsCopy(currentDataObject.violations);

            let breaksCopy = getBreaksCopy(currentDataObject.breaks);

            let periods = getStatusLogPeriods(
                hasRenderLimit ? statusLogs.filter(log => new Date(log.dateTime).getTime() < endDate.getTime()) : statusLogs,
                endDate
            );
            let includeVerified = currentDataObject.options.includeVerified;

            periods.forEach((period, index) => {
                if (index > 0) {
                    addConnectingLine(period.fromDate, periods[index - 1].index, period.index);
                }
                buildBarFromData(
                    period.fromDate,
                    period.toDate,
                    period.index,
                    !includeVerified || period.isVerified,
                    period.isEdited,
                    false,
                    false,
                    period.logId,
                    period.isSituation
                );
                addViolationBar(period.fromDate, period.toDate, period.index, period.logStatus, period.logId, violationListCopy);
                addBreakBar(period.fromDate, period.toDate, period.index, period.logId, breaksCopy);
            });

            statusCounters = periods.reduce((counter, period) => {
                let duration = period.toDate.valueOf() - period.fromDate.valueOf();
                if (duration < 0) {
                    duration = ["Work", "WorkExemption"].indexOf(period.logStatus) > -1 ? 15 * 1000 * 60 : 0;
                }
                counter[period.index] += duration;
                return counter;
            }, statusCounters);

            let descriptor = new GraphDescriptor(translate);
            driver.setDescriptorId(descriptor.id);
            let [x, tileFontSize, lastGraphLineYPosition] = renderSum(color.outerLineColor, descriptor);
            addAriaLabelForGraph();
            createTotalHoursLabel(x, tileFontSize, lastGraphLineYPosition, color);
            descriptor.pasteInto(containerDivObject);
            containerDivObject.appendChild(driver.getCanvas());
        },
        getBreaksCopy = (breaks: IBreakPeriod[]) => {
            let breaksList: IBreakPeriod[] = (breaks || []).map(v => ({ ...v }));
            breaksList.sort((a, b) => {
                let dateA = a.fromDate;
                let dateB = b.fromDate;
                return dateA.getTime() - dateB.getTime();
            });
            return breaksList;
        },
        getStatusLogPeriods = function (statusLogs: IGraphLog[], endDate: Date) {
            let periods: IRenderPeriod[] = [];

            for (let i = 1; i <= statusLogs.length; i++) {
                let prevLog = statusLogs[i - 1],
                    logStatus = prevLog.status,
                    fromDate = new Date(prevLog.dateTime),
                    toDate = statusLogs[i] ? new Date(statusLogs[i].dateTime) : endDate,
                    isSituation = false;

                if (logStatus === "YM") {
                    isSituation = true;
                    logStatus = "ON";
                } else if (logStatus === "PC" || logStatus === "PC_Exempted") {
                    isSituation = true;
                    logStatus = "OFF";
                } else if (logStatus === "WorkExemption") {
                    isSituation = true;
                    logStatus = "Work";
                }
                periods.push({
                    fromDate,
                    toDate,
                    logStatus,
                    index: statuses.indexOf(logStatus),
                    isVerified: !!prevLog.verifyDateTime,
                    isEdited: !!prevLog._isEdited,
                    logId: prevLog.id,
                    isSituation
                });
            }

            return periods;
        },
        removeHandlers = function () {
            if (currentDataObject === undefined) {
                return;
            }

            driver && driver.removeListeners();
        },
        computeDimensions = (width: number, labels: ILegendLabel[], fontScale: number): IDimensions => {
            let computeBiggestWordLength = (fontSizeNum: number) => {
                let longestWord = getLongestString(labels);
                let elem = document.createElement("span");

                elem.style.fontSize = `${fontSizeNum}px`;
                elem.style.position = "absolute";
                elem.style.top = "-100px";
                elem.innerHTML = longestWord.toUpperCase();
                document.body.appendChild(elem);

                let biggestWidth = elem.clientWidth;
                document.body.removeChild(elem);

                return Math.ceil(biggestWidth);
            };

            // Adapting font size to the small screens
            let fontMultiplier = width < 700 ? 1.5 : 1;
            let fontSize = width * 0.002 * fontScale * fontMultiplier;
            let biggestLabelsLength = computeBiggestWordLength(fontSize);
            let spaceBetween = fontSize * 0.5;
            let rectSide = fontSize * 1.5;
            let columnInterval = biggestLabelsLength + spaceBetween + rectSide + fontSize;
            let columns = Math.floor(width / columnInterval);
            let rowInterval = fontSize;
            let labelRows = Math.ceil(labels.length / columns);
            let rowGap = fontSize * 1.5;
            let rows = labels.reduce((counter, _0, index, array) => {
                let rowsAmount = counter;
                let start = index * columns;
                let row = array.slice(start, start + columns);

                return (rowsAmount += findBiggestLength(row));
            }, 0);

            return {
                width,
                fontSize,
                rectSide,
                spaceBetween,
                leftMargin: width * 0.05,
                rowGap,
                rowInterval,
                labelsInterval: rowInterval * 1.2,
                columnInterval,
                columns,
                labelRows: Math.ceil(labels.length / columns),
                rows,
                height: rows * rowInterval + rowGap * labelRows
            };
        },
        splitToRows = (labels: ILegendLabel[], dimensions: IDimensions) => {
            let splittedLabels: ILegendLabel[][] = [];

            for (let start = 0; start < labels.length; start += dimensions.columns) {
                splittedLabels.push(labels.slice(start, start + dimensions.columns));
            }

            return splittedLabels;
        },
        findBiggestLength = (array: (ILegendLabel | string[])[]) => {
            return array.reduce((max: number, row: ILegendLabel | string[]) => {
                let length = Array.isArray(row) ? row.length : row.labels.length;
                return max < length ? length : max;
            }, 0);
        },
        getLongestString = (labels: TLegendLabelTree[]) => {
            let toFlatArray = (array: TLegendLabelTree[]): string[] => {
                return array.reduce<string[]>((acc, subEntity) => {
                    if (typeof subEntity === "string") {
                        acc.push(subEntity);
                    } else {
                        return acc.concat(toFlatArray(Array.isArray(subEntity) ? subEntity : subEntity.labels));
                    }

                    return acc;
                }, []);
            };

            let flatLabels = toFlatArray(labels);
            return flatLabels.reduce((max, row) => (max.length < row.length ? row : max));
        },
        createLegendRects = (labels: ILegendLabel[][], dimensions: IDimensions, startingHeight: number) => {
            let rowsPassed = 0;

            labels.forEach((row, rowIndex) => {
                let biggestAmountOfLabels = findBiggestLength(row);

                row.forEach((record, recordIndex) => {
                    let topGab = ((biggestAmountOfLabels - 1) * dimensions.rowInterval) / 2;
                    let x = recordIndex * dimensions.columnInterval + dimensions.leftMargin;
                    let y = startingHeight + rowsPassed * dimensions.rowInterval + rowIndex * dimensions.rowGap + topGab;

                    driver.rect(x, y, dimensions.rectSide, dimensions.rectSide, record.style, `legend-${record.id}-rect`);
                });

                rowsPassed += findBiggestLength(row);
            });
        },
        createLegendLabels = (labels: ILegendLabel[][], dimensions: IDimensions, startingHeight: number) => {
            let rowsPassed = 1;

            labels.forEach((row, rowIndex) => {
                let biggestAmountOfLabels = findBiggestLength(row);

                row.forEach((column, columnIndex) => {
                    let labelsAmount = column.labels.length;
                    column.labels.forEach((label, labelIndex) => {
                        let topGab = ((biggestAmountOfLabels - labelsAmount) * dimensions.rowInterval) / 2;
                        let fontWeight: TFontWeight = currentDataObject.options.fontBold ? "bold" : "normal";
                        let x = dimensions.spaceBetween + dimensions.rectSide + columnIndex * dimensions.columnInterval + dimensions.leftMargin;
                        let y =
                            startingHeight +
                            rowsPassed * dimensions.rowInterval +
                            labelIndex * dimensions.labelsInterval +
                            rowIndex * dimensions.rowGap +
                            topGab;

                        driver.text(
                            x,
                            y,
                            label.toUpperCase(),
                            dimensions.fontSize,
                            fontWeight,
                            "graphLabel",
                            column.style.strokeColor,
                            `legend-${column.id}-label`
                        );
                    });
                });

                rowsPassed += findBiggestLength(row);
            });
        },
        renderLegend = (dimensions: IDimensions, labels: ILegendLabel[], startingHeight: number) => {
            let splittedLabels = splitToRows(labels, dimensions);
            createLegendLabels(splittedLabels, dimensions, startingHeight + legendOffset);
            createLegendRects(splittedLabels, dimensions, startingHeight + legendOffset);
        },
        // use this method for unit tests
        getTotalHours = () => {
            let counters = getStatusCounters();
            return counters.reduce((a, b) => a + b) / (60 * 60 * 1000);
        },
        createTotalHoursLabel = (x: number, fontSize: number, lastGraphLineYPosition: number, colors: IGraphColors) => {
            let totalHours = getTotalHours();

            driver.line(x, lastGraphLineYPosition, driver.getWidth() - 20, lastGraphLineYPosition, colors.outerLineColor, 2, MAIN_GROUP_ID);
            driver.text(
                x,
                lastGraphLineYPosition + fontSize * 1.2,
                getGraphTimeStampLabel(getHoursFromDecimalHours(totalHours), getMinutesFromDecimalHours(totalHours)),
                fontSize,
                "bold",
                "graphLabel",
                colors.outerLineColor,
                "bar-hos-graph-status_totalHours",
                MAIN_GROUP_ID
            );
        };

    return {
        createGraph: (
            divObject: HTMLElement,
            dataObject: IGraphData,
            options: IGraphOptions = {},
            clickCallback?: TGraphClickHandler,
            violations: IViolation[] = [],
            breaks: IBreakPeriod[] = []
        ) => {
            removeHandlers();
            statusCounters = [0, 0, 0, 0, 0];
            currentDataObject = {
                data: dataObject,
                violations: options.includeViolations ? violations : [],
                breaks: options.includeBreaks ? breaks : [],
                clickHandler: clickCallback,
                options: { ...getDefaultOptions(), ...options }
            };
            containerDivObject = divObject;
            statuses = currentDataObject.options.statuses || [];
            allStatuses = currentDataObject.options.allStatuses || [];
            render();
        },
        removeHandlers,
        graphState: newDataObject => {
            if (newDataObject !== null && newDataObject !== undefined) {
                let selectResult = containerDivObject.querySelectorAll("svg.svgGraph");

                statusCounters = [0, 0, 0, 0, 0];

                if (selectResult.length !== 1) {
                    throw selectResult.length === 0
                        ? new Error(translate("No graph found."))
                        : new Error(translate("More than one graph in the same container"));
                }

                currentDataObject = newDataObject;
                render();
            }
            return currentDataObject;
        },
        //For tests
        getStatusCounters,
        getTotalHours,
        getHoursAndMinutesFromStatusCounters
    };
}

export let hosGraphConstructorStrings = [
    "No graph found.",
    "More than one graph in the same container",
    "Total {0} duration: {1} {2} {3}",
    "Duty Status Graph and HOS Logs",
    "h",
    "{minutes}m",
    "hours",
    "seconds",
    "HOS Graph showing:",
    "Total Off Duty: {0}",
    "Total Sleeper Berth: {0}",
    "Total Drive: {0}",
    "Total On Duty: {0}",
    "Total Wait time: {0}",
    "Total Work: {0}",
    "Total Rest: {0}",
    "Grand total: {0}"
];
